import { stringify } from 'query-string';
import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useHistory } from 'react-router';
import { upperCase } from 'lodash';

import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { FormElement, Label } from '@dashboard/components/Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import Link from '@dashboard/components/Link';
import Dialog from '@dashboard/components/Dialog';
import Button from '@dashboard/components/Button';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import Spinner from '@dashboard/components/Spinner';
import { Badge } from '@dashboard/components/ui/badge';
import { toast } from '@dashboard/context/ToastContext';
import { useAdminIntegrationLogs } from '@dashboard/hooks/queries/useAdminQueries';
import { useResendIntegrationLog } from '@dashboard/hooks/queries/useAdminMutations';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { WEBHOOK_EVENT_TYPES } from '@dashboard/enums/webhook-events';

export default function IntegrationLogs() {
  const history = useHistory();
  const query = useUrlQuery();

  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(
    query.sortDirection ? Number(query.sortDirection) : -1,
  );
  const [status, setStatus] = useState(query.status || 'all');
  const [event, setEvent] = useState(query.event || 'all');
  const [integrationType, setIntegrationType] = useState(query.integrationType || 'all');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        status,
        event,
        integrationType,
      }),
    });
  }, [sortBy, sortDirection, status, event, integrationType]);

  const filters = {
    sortBy,
    sortDirection,
    status,
    event,
    integrationType,
    limit: 20,
  };

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useAdminIntegrationLogs(filters);

  const resendMutation = useResendIntegrationLog({
    onSuccess: () => {
      toast({
        title: 'Integration log resent successfully',
        type: 'success',
      });
    },
    onError: error => {
      toast({
        title: 'Failed to resend integration log',
        description: error?.message,
        type: 'error',
      });
    },
  });

  // Convert sortBy/sortDirection to TanStack SortingState
  const sorting = useMemo(
    () => [
      {
        id: sortBy,
        desc: sortDirection === -1,
      },
    ],
    [sortBy, sortDirection],
  );

  // Handle sorting changes from DataGrid
  const handleSortingChange = updater => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      setSortBy(id);
      setSortDirection(desc ? -1 : 1);
    }
  };

  // Flatten pages data for DataGrid
  const flatData = useMemo(() => data?.pages?.flatMap(page => page.data) ?? [], [data]);

  const getStatusVariant = status => {
    switch (status) {
      case 'succeeded':
        return 'success';
      case 'failed':
        return 'destructive';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Define columns
  const columns = useMemo(
    () => [
      {
        accessorKey: 'sellerId',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Seller" />,
        cell: ({ row }) => (
          <Link to={`/admin/merchants/${row.original.seller?._id}`}>
            {row.original.seller?.displayName || row.original.seller?.name || row.original.sellerId}
          </Link>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'pageId',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Page" />,
        cell: ({ row }) => (
          <Link to={`/pages/${row.original.page?._id}`}>{row.original.page?.name}</Link>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'integrationType',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Type" />,
        cell: ({ row }) => (
          <div className="flex gap-x-1">
            <Badge variant="info">{upperCase(row.getValue('integrationType'))}</Badge>
          </div>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'event',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Event" />,
        cell: ({ row }) => {
          const event = row.getValue('event');
          return event ? <Badge variant="outline">{upperCase(event)}</Badge> : null;
        },
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Date" />,
        cell: ({ row }) => format(new Date(row.getValue('createdAt')), 'yyyy-MM-dd HH:mm'),
        enableSorting: true,
      },
      {
        accessorKey: 'deliveryStatus',
        header: ({ column }) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }) => {
          const log = row.original;
          return (
            <div className="flex items-center gap-x-2">
              <Badge variant={getStatusVariant(log.deliveryStatus)}>
                {upperCase(log.deliveryStatus)}
              </Badge>

              {log.response && log.deliveryStatus === 'failed' && (
                <Dialog
                  title={`Error for log ID ${log._id}`}
                  trigger={
                    <Button appearance="light" size="small">
                      View error
                    </Button>
                  }
                  confirmButton={null}
                  cancelButton={null}
                >
                  <div>Status Code: {log.statusCode}</div>
                  <div>Retries: {log.retries}</div>
                  <div className="text-red-600 dark:text-red-400 mt-2">{log.response}</div>

                  <div className="mt-4">
                    <ShadcnButton
                      size="sm"
                      onClick={() =>
                        resendMutation.mutate({
                          integrationId: log.integrationId,
                          integrationLogId: log._id,
                        })
                      }
                      disabled={resendMutation.isLoading}
                    >
                      Resend Integration
                      {resendMutation.isLoading && <Spinner />}
                    </ShadcnButton>
                  </div>
                </Dialog>
              )}
            </div>
          );
        },
        enableSorting: true,
      },
    ],
    [resendMutation],
  );

  // Filter controls as custom actions
  const filterControls = (
    <div className="flex items-end gap-x-4">
      <FormElement className="!mb-0">
        <Label>Integration Type</Label>
        <Select value={integrationType} onValueChange={setIntegrationType}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="webhook">Webhook</SelectItem>
            <SelectItem value="googleSheets">Google Sheets</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label>Event</Label>
        <Select value={event} onValueChange={setEvent}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value={WEBHOOK_EVENT_TYPES.CONVERSION}>Conversion</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>

      <FormElement className="!mb-0">
        <Label>Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="succeeded">Succeeded</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </FormElement>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>Integration Logs</h1>
        {filterControls}
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No integration logs found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
}
